/**
 * EditorCameraController - Edit-mode orbit camera for the StorySplat editor
 *
 * Provides an independent orbit camera for scene editing, separate from the
 * tour/explore camera. Also renders an FPV camera visualization mesh showing
 * where the tour camera is positioned.
 *
 * Only instantiated by the editor, never by the viewer.
 */

import * as pc from 'playcanvas';
import { CameraControls } from '../dynamic-viewer/CameraControls';

export interface EditorCameraControllerConfig {
  /** Initial orbit distance from target */
  orbitDistance?: number;
  /** Movement speed */
  moveSpeed?: number;
  /** Rotation sensitivity */
  rotateSensitivity?: number;
  /** Whether to show FPV camera visualization */
  showFPVVisualization?: boolean;
  /** Gizmo layer to add to edit camera for gizmo visibility */
  gizmoLayer?: pc.Layer;
}

export class EditorCameraController {
  private app: pc.Application;
  private cameraControls: CameraControls;
  private editCamera: pc.Entity;
  private tourCamera: pc.Entity;
  private fpvEntity: pc.Entity | null = null;
  private _enabled = false;
  private config: EditorCameraControllerConfig;

  // Orbit state
  private orbitTarget = new pc.Vec3();
  private orbitDistance = 10;
  private orbitYaw = 0;   // degrees
  private orbitPitch = -30; // degrees
  private isDragging = false;
  private isPanning = false;
  private lastMouseX = 0;
  private lastMouseY = 0;

  // Bound event handlers for cleanup
  private _onMouseDown: ((e: MouseEvent) => void) | null = null;
  private _onMouseMove: ((e: MouseEvent) => void) | null = null;
  private _onMouseUp: ((e: MouseEvent) => void) | null = null;
  private _onWheel: ((e: WheelEvent) => void) | null = null;
  private _onContextMenu: ((e: MouseEvent) => void) | null = null;
  private _onFPVUpdate: (() => void) | null = null;

  constructor(
    app: pc.Application,
    tourCamera: pc.Entity,
    cameraControls: CameraControls,
    config: EditorCameraControllerConfig = {}
  ) {
    this.app = app;
    this.tourCamera = tourCamera;
    this.cameraControls = cameraControls;
    this.config = config;

    // Create a separate edit camera entity
    this.editCamera = new pc.Entity('editCamera');
    const tourCam = tourCamera.camera;
    this.editCamera.addComponent('camera', {
      clearColor: tourCam?.clearColor || new pc.Color(0.1, 0.1, 0.1),
      fov: tourCam?.fov || 60,
      nearClip: tourCam?.nearClip || 0.1,
      farClip: tourCam?.farClip || 1000
    });

    // Add gizmo layer to edit camera if provided
    if (config.gizmoLayer && this.editCamera.camera) {
      const layers = this.editCamera.camera.layers;
      if (!layers.includes(config.gizmoLayer.id)) {
        this.editCamera.camera.layers = [...layers, config.gizmoLayer.id];
      }
    }

    // Position edit camera near tour camera initially
    const pos = tourCamera.getPosition();
    this.editCamera.setPosition(pos.x, pos.y + 5, pos.z + 10);
    this.editCamera.lookAt(pos);
    this.editCamera.enabled = false;

    app.root.addChild(this.editCamera);

    // Create FPV visualization (small cone/pyramid showing tour camera)
    if (config.showFPVVisualization !== false) {
      this.createFPVVisualization();
    }
  }

  private createFPVVisualization(): void {
    this.fpvEntity = new pc.Entity('fpv-camera-viz');

    // Use a cone to represent the camera frustum
    this.fpvEntity.addComponent('render', {
      type: 'cone',
      castShadows: false,
      receiveShadows: false
    });

    this.fpvEntity.setLocalScale(0.3, 0.5, 0.3);
    this.fpvEntity.enabled = false;
    this.app.root.addChild(this.fpvEntity);

    // Update FPV position each frame
    this._onFPVUpdate = () => {
      if (this.fpvEntity && this._enabled) {
        const tourPos = this.tourCamera.getPosition();
        const tourRot = this.tourCamera.getRotation();
        this.fpvEntity.setPosition(tourPos);
        this.fpvEntity.setRotation(tourRot);
        // Rotate cone to point forward (cone points up by default)
        // rotateLocal applies on top of the absolute setRotation each frame
        this.fpvEntity.rotateLocal(90, 0, 0);
      }
    };
    this.app.on('update', this._onFPVUpdate);
  }

  /** Update the edit camera position from orbit parameters */
  private updateOrbitCamera(): void {
    const yawRad = this.orbitYaw * pc.math.DEG_TO_RAD;
    const pitchRad = this.orbitPitch * pc.math.DEG_TO_RAD;

    const x = this.orbitTarget.x + this.orbitDistance * Math.cos(pitchRad) * Math.sin(yawRad);
    const y = this.orbitTarget.y + this.orbitDistance * Math.sin(pitchRad);
    const z = this.orbitTarget.z + this.orbitDistance * Math.cos(pitchRad) * Math.cos(yawRad);

    this.editCamera.setPosition(x, y, z);
    this.editCamera.lookAt(this.orbitTarget);
  }

  /** Attach mouse input handlers for orbit/pan/zoom on the edit camera */
  private attachInputHandlers(): void {
    const canvas = this.app.graphicsDevice.canvas;

    this._onMouseDown = (e: MouseEvent) => {
      // Left button = orbit, Middle or Shift+Left = pan
      if (e.button === 0 && !e.shiftKey) {
        this.isDragging = true;
        this.isPanning = false;
      } else if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
        this.isDragging = true;
        this.isPanning = true;
        e.preventDefault();
      } else if (e.button === 2) {
        this.isDragging = true;
        this.isPanning = true;
      }
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    };

    this._onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return;

      const dx = e.clientX - this.lastMouseX;
      const dy = e.clientY - this.lastMouseY;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;

      if (this.isPanning) {
        // Pan: move orbit target in camera-local X/Y
        const panSpeed = this.orbitDistance * 0.002;
        const camRight = this.editCamera.right;
        const camUp = this.editCamera.up;
        this.orbitTarget.x -= camRight.x * dx * panSpeed - camUp.x * dy * panSpeed;
        this.orbitTarget.y -= camRight.y * dx * panSpeed - camUp.y * dy * panSpeed;
        this.orbitTarget.z -= camRight.z * dx * panSpeed - camUp.z * dy * panSpeed;
      } else {
        // Orbit: rotate yaw/pitch
        const sensitivity = this.config.rotateSensitivity || 0.3;
        this.orbitYaw -= dx * sensitivity;
        this.orbitPitch += dy * sensitivity;
        // Clamp pitch to avoid flipping
        this.orbitPitch = pc.math.clamp(this.orbitPitch, -89, 89);
      }

      this.updateOrbitCamera();
    };

    this._onMouseUp = () => {
      this.isDragging = false;
      this.isPanning = false;
    };

    this._onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomSpeed = this.orbitDistance * 0.1;
      this.orbitDistance += e.deltaY > 0 ? zoomSpeed : -zoomSpeed;
      this.orbitDistance = Math.max(0.5, this.orbitDistance);
      this.updateOrbitCamera();
    };

    this._onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this._onContextMenu);
  }

  /** Detach mouse input handlers */
  private detachInputHandlers(): void {
    const canvas = this.app.graphicsDevice.canvas;
    if (this._onMouseDown) canvas.removeEventListener('mousedown', this._onMouseDown);
    if (this._onMouseMove) canvas.removeEventListener('mousemove', this._onMouseMove);
    if (this._onMouseUp) canvas.removeEventListener('mouseup', this._onMouseUp);
    if (this._onWheel) canvas.removeEventListener('wheel', this._onWheel);
    if (this._onContextMenu) canvas.removeEventListener('contextmenu', this._onContextMenu);
  }

  /** Enable edit camera mode (switches from tour camera to edit camera) */
  enable(): void {
    this._enabled = true;
    this.tourCamera.enabled = false;
    this.editCamera.enabled = true;
    this.cameraControls.disable();

    // Initialize orbit from current tour camera position
    const tourPos = this.tourCamera.getPosition();
    const tourForward = this.tourCamera.forward;

    // Set orbit target 5 units in front of tour camera
    this.orbitTarget.set(
      tourPos.x + tourForward.x * 5,
      tourPos.y + tourForward.y * 5,
      tourPos.z + tourForward.z * 5
    );
    this.orbitDistance = 5;

    // Compute yaw/pitch from tour camera direction
    const dirX = tourPos.x - this.orbitTarget.x;
    const dirY = tourPos.y - this.orbitTarget.y;
    const dirZ = tourPos.z - this.orbitTarget.z;
    this.orbitYaw = Math.atan2(dirX, dirZ) * pc.math.RAD_TO_DEG;
    this.orbitPitch = Math.atan2(dirY, Math.sqrt(dirX * dirX + dirZ * dirZ)) * pc.math.RAD_TO_DEG;

    this.updateOrbitCamera();
    this.attachInputHandlers();

    if (this.fpvEntity) {
      this.fpvEntity.enabled = true;
    }
  }

  /** Disable edit camera mode (switches back to tour camera) */
  disable(): void {
    this._enabled = false;
    this.editCamera.enabled = false;
    this.tourCamera.enabled = true;
    this.detachInputHandlers();

    if (this.fpvEntity) {
      this.fpvEntity.enabled = false;
    }
  }

  get enabled(): boolean {
    return this._enabled;
  }

  /** Get the edit camera entity */
  getCamera(): pc.Entity {
    return this.editCamera;
  }

  /** Focus edit camera on a target point */
  focusOn(target: pc.Vec3): void {
    this.orbitTarget.copy(target);
    this.orbitDistance = this.config.orbitDistance || 10;
    this.updateOrbitCamera();
  }

  /** Sync edit camera clear color with tour camera */
  syncClearColor(): void {
    if (this.editCamera.camera && this.tourCamera.camera) {
      this.editCamera.camera.clearColor = this.tourCamera.camera.clearColor;
    }
  }

  /** Update FOV on both cameras */
  setFOV(fov: number): void {
    if (this.editCamera.camera) this.editCamera.camera.fov = fov;
    if (this.tourCamera.camera) this.tourCamera.camera.fov = fov;
  }

  destroy(): void {
    this.detachInputHandlers();
    this.disable();
    if (this._onFPVUpdate) {
      this.app.off('update', this._onFPVUpdate);
      this._onFPVUpdate = null;
    }
    this.editCamera.destroy();
    if (this.fpvEntity) {
      this.fpvEntity.destroy();
      this.fpvEntity = null;
    }
  }
}
